import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Download, Smartphone, Monitor, Globe } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Detect device type
    const ua = navigator.userAgent;
    setIsIOS(/iPad|iPhone|iPod/.test(ua) && !window.MSStream);
    setIsAndroid(/Android/.test(ua));
    
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setCanInstall(false);
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setCanInstall(false);
        setShowInstallModal(false);
      }
      setDeferredPrompt(null);
    }
  };

  return (
    <div className="landing-m3" data-testid="landing-page">
      <style>{`
        :root {
          --black: #000;
          --gold: #D4AF37;
          --gold-light: #E6C25D;
          --white: #F5F5F5;
          --gray-light: #E0E0E0;
          --transition: all .4s cubic-bezier(.165,.84,.44,1);
        }
        .landing-m3 * { box-sizing: border-box; margin: 0; padding: 0; }
        .landing-m3 { font-family: Inter, sans-serif; background: var(--black); color: var(--white); line-height: 1.6; overflow-x: hidden; min-height: 100vh; }
        .landing-m3 h1, .landing-m3 h2, .landing-m3 h3, .landing-m3 h4, .landing-m3 h5, .landing-m3 h6 { font-family: 'Playfair Display', serif; font-weight: 700; }
        .landing-m3 .container { max-width: 1400px; margin: 0 auto; padding: 0 2rem; width: 100%; }
        .landing-m3 header.nav-header { position: fixed; top: 0; width: 100%; z-index: 1000; padding: 1.5rem 0; background: rgba(0,0,0,.8); backdrop-filter: blur(10px); border-bottom: 1px solid rgba(212,175,55,0.1); }
        .landing-m3 .header-container { display: flex; justify-content: space-between; align-items: center; }
        .landing-m3 .logo { font-size: 2.2rem; font-weight: 800; background: linear-gradient(135deg, var(--white) 0%, var(--gold) 80%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; text-decoration: none; text-shadow: 0 0 15px rgba(212,175,55,0.3); position: relative; z-index: 10; }
        .landing-m3 .nav-links { display: flex; gap: 2.5rem; align-items: center; }
        .landing-m3 .nav-links a { color: var(--white); text-decoration: none; font-weight: 500; font-size: .95rem; text-transform: uppercase; position: relative; cursor: pointer; }
        .landing-m3 .nav-links a::after { content: ''; position: absolute; bottom: -5px; left: 0; width: 0; height: 1px; background: var(--gold); transition: var(--transition); }
        .landing-m3 .nav-links a:hover::after { width: 100%; }
        .landing-m3 .language-selector { background: transparent; color: var(--white); border: 1px solid var(--gold); padding: .5rem 1rem; border-radius: 4px; cursor: pointer; font-size: .9rem; }
        .landing-m3 a { color: var(--gold); }
        .landing-m3 .hero { height: 100vh; display: flex; align-items: center; position: relative; overflow: hidden; }
        .landing-m3 .hero-image { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(rgba(0,0,0,.5),rgba(0,0,0,.8)), url('https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=2070&auto=format&fit=crop') center/cover no-repeat; z-index: -1; }
        .landing-m3 .hero-content { max-width: 650px; padding-top: 4rem; }
        .landing-m3 .hero h1 { font-size: 4.5rem; line-height: 1.1; margin-bottom: 1.5rem; background: linear-gradient(to right,var(--white),var(--gold-light)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .landing-m3 .hero p { font-size: 1.25rem; color: var(--gray-light); margin-bottom: 2.5rem; font-family: 'Playfair Display', serif; font-weight: 300; }
        .landing-m3 .hero-cta { display: inline-block; background: var(--gold); color: var(--black); padding: .8rem 2.5rem; text-decoration: none; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; font-size: .9rem; border-radius: 4px; transition: var(--transition); cursor: pointer; border: none; margin-right: 1rem; }
        .landing-m3 .hero-cta:hover { transform: translateY(-3px); box-shadow: 0 10px 30px rgba(212,175,55,.3); }
        .landing-m3 .hero-cta-app { background: linear-gradient(45deg, var(--gold), var(--gold-light)); animation: pulse 2s infinite; }
        @keyframes pulse { 0% { box-shadow: 0 0 20px rgba(212,175,55,.2); } 50% { box-shadow: 0 0 30px rgba(212,175,55,.5); } 100% { box-shadow: 0 0 20px rgba(212,175,55,.2); } }
        .landing-m3 section { padding: 8rem 0; }
        .landing-m3 .section-title { text-align: center; margin-bottom: 5rem; }
        .landing-m3 .section-title h2 { font-size: 3.5rem; display: inline-block; position: relative; }
        .landing-m3 .section-title h2::after { content: ''; position: absolute; bottom: -15px; left: 50%; transform: translateX(-50%); width: 80px; height: 2px; background: var(--gold); }
        .landing-m3 .section-title p { font-size: 1.2rem; color: var(--gray-light); max-width: 700px; margin: 2rem auto 0; font-weight: 300; }
        .landing-m3 .about-content { display: grid; grid-template-columns: 1fr 1fr; gap: 4rem; align-items: center; }
        .landing-m3 .about-image { width: 100%; height: 500px; background: linear-gradient(45deg,var(--black),#1a1a1a); border: 1px solid var(--gold); position: relative; overflow: hidden; border-radius: 8px; }
        .landing-m3 .about-image::before { content: ""; position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070&auto=format&fit=crop') center/cover no-repeat; }
        .landing-m3 .about-text h3 { font-size: 2.5rem; margin-bottom: 1.5rem; background: linear-gradient(to right,var(--white),var(--gold)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .landing-m3 .services-grid { display: grid; grid-template-columns: repeat(auto-fit,minmax(300px,1fr)); gap: 3rem; }
        .landing-m3 .service-card { background: rgba(30,30,30,.85); border: 1px solid rgba(212,175,55,.08); padding: 2.5rem; border-radius: 10px; transition: var(--transition); box-shadow: 0 18px 40px rgba(212,175,55,0.08); }
        .landing-m3 .service-card:hover { transform: translateY(-12px); border-color: var(--gold); box-shadow: 0 30px 60px rgba(212,175,55,.22); }
        .landing-m3 .service-card h3 { background: linear-gradient(to right,var(--white),var(--gold)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .landing-m3 .clients-grid { display: grid; grid-template-columns: repeat(auto-fit,minmax(250px,1fr)); gap: 2rem; text-align: center; background: linear-gradient(rgba(0,0,0,.7),rgba(0,0,0,.9)), url('https://images.unsplash.com/photo-1559027868-a8a4452c5e8a?q=80&w=2070&auto=format&fit=crop'); background-size: cover; background-position: center; padding: 4rem 2rem; border-radius: 12px; border: 1px solid rgba(212,175,55,.2); }
        .landing-m3 .client-item { padding: 2rem; background: rgba(0,0,0,.6); border: 1px solid rgba(212,175,55,.08); height: 260px; display: flex; flex-direction: column; align-items: center; justify-content: center; border-radius: 12px; position: relative; }
        .landing-m3 .client-logo { width: 130px; height: 130px; margin-bottom: 1rem; display: flex; align-items: center; justify-content: center; border-radius: 8px; overflow: hidden; border: 3px solid rgba(212,175,55,.18); background: linear-gradient(135deg,var(--black),#1a1a1a); }
        .landing-m3 .client-logo img { width: 100%; height: 100%; object-fit: cover; border-radius: 8px; }
        .landing-m3 .package-card { background: rgba(15,15,15,.8); border: 2px solid var(--gold); padding: 3rem 2rem; text-align: center; border-radius: 8px; transition: var(--transition); margin-bottom: 2rem; }
        .landing-m3 .package-card.black-label-special { background: linear-gradient(135deg, rgba(21,21,21,.95), rgba(10,10,10,.95)); border: 3px solid var(--gold-light); transform: scale(1.05); box-shadow: 0 0 40px rgba(212,175,55,.2); max-width: 350px; margin: 0 auto 2rem; animation: pulse 2s infinite; }
        .landing-m3 .package-title { font-size: 1.8rem; margin-bottom: 1rem; background: linear-gradient(to right,var(--white),var(--gold)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .landing-m3 .package-subtitle { font-weight: 700; font-size: 1.2rem; margin-bottom: 1.5rem; color: var(--gold-light); }
        .landing-m3 .package-price { font-size: 2.5rem; font-weight: 700; margin: 1.5rem 0; color: var(--gold); }
        .landing-m3 .package-features { list-style: none; margin: 2rem 0; text-align: left; }
        .landing-m3 .package-features li { padding: .8rem 0; border-bottom: 1px solid rgba(255,255,255,.1); color: var(--gray-light); padding-left: 1.8rem; position: relative; }
        .landing-m3 .package-features li::before { content: '✓'; position: absolute; left: 0; top: .8rem; color: var(--gold); font-weight: bold; }
        .landing-m3 .black-label-special .package-features li::before { content: '✦'; color: var(--gold-light); }
        .landing-m3 .package-btn { background: var(--gold); color: var(--black); border: none; padding: .8rem 2rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; border-radius: 4px; cursor: pointer; transition: var(--transition); width: 100%; margin-bottom: 0.5rem; display: block; text-decoration: none; text-align: center; }
        .landing-m3 .package-btn:last-child { margin-bottom: 0; }
        .landing-m3 .package-btn:hover { transform: translateY(-3px); box-shadow: 0 10px 30px rgba(212,175,55,.3); }
        .landing-m3 .black-label-special .package-btn { background: linear-gradient(45deg,var(--gold),var(--gold-light)); padding: 1rem 2.2rem; font-size: 1.1rem; }
        .landing-m3 .consultation-section { background: linear-gradient(rgba(5,5,5,.9),rgba(0,0,0,.95)); padding: 8rem 0; border-top: 1px solid rgba(212,175,55,.1); }
        .landing-m3 .consultation-cta-btn { background: var(--gold); color: var(--black); border: none; padding: 1rem 2rem; font-weight: 700; letter-spacing: 1px; border-radius: 4px; cursor: pointer; }
        .landing-m3 footer { background: linear-gradient(rgba(10,10,10,.95),rgba(0,0,0,.98)); padding: 6rem 0 3rem; border-top: 1px solid rgba(212,175,55,.15); }
        .landing-m3 .footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 4rem; margin-bottom: 4rem; }
        .landing-m3 .footer-column h3 { font-size: 1.5rem; margin-bottom: 2rem; color: var(--gold); }
        .landing-m3 .footer-column p, .landing-m3 .footer-column li { color: var(--gray-light); line-height: 1.8; margin-bottom: 1rem; }
        .landing-m3 .footer-column ul { list-style: none; }
        .landing-m3 .footer-socials { display: flex; gap: 1rem; margin-top: 2rem; }
        .landing-m3 .fixed-cta { position: fixed; bottom: 0; left: 0; width: 100%; background: rgba(0,0,0,.95); border-top: 1px solid rgba(212,175,55,.2); padding: 1rem 0; z-index: 999; backdrop-filter: blur(10px); }
        .landing-m3 .fixed-cta-content { display: flex; justify-content: space-between; align-items: center; max-width: 1400px; margin: 0 auto; padding: 0 2rem; }
        .landing-m3 .fixed-cta-button { background: var(--gold); color: var(--black); border: none; padding: .6rem 1.5rem; font-weight: 700; border-radius: 4px; cursor: pointer; }
        .landing-m3 .luxury-content { display: grid; grid-template-columns: 1fr 1fr; gap: 2.5rem; align-items: center; }
        .landing-m3 .luxury-image { width: 100%; height: 420px; border-radius: 12px; background: url('https://images.unsplash.com/photo-1504384308090-c894fdcc538d?q=80&w=2070&auto=format&fit=crop') center/cover no-repeat; box-shadow: 0 20px 60px rgba(0,0,0,.6); border: 1px solid rgba(212,175,55,.08); }
        .landing-m3 .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2.5rem; align-items: start; }
        .landing-m3 .contact-form { background: rgba(255,255,255,.03); padding: 2rem; border-radius: 10px; border: 1px solid rgba(212,175,55,.06); }
        .landing-m3 .contact-form input, .landing-m3 .contact-form textarea { width: 100%; padding: 1rem; background: rgba(255,255,255,.05); border: 1px solid rgba(212,175,55,.08); color: #fff; border-radius: 6px; margin-bottom: 1.5rem; }
        .landing-m3 .mobile-menu-btn { display: none; background: none; border: none; color: var(--gold); font-size: 2rem; cursor: pointer; }
        .landing-m3 .pwa-banner { background: linear-gradient(to right, rgba(212,175,55,0.1), rgba(212,175,55,0.05)); border: 1px solid rgba(212,175,55,0.2); padding: 1rem 2rem; display: flex; align-items: center; justify-content: space-between; margin: 1rem 2rem; border-radius: 8px; }
        .landing-m3 .app-access-cta { position: fixed; top: 80px; right: 20px; z-index: 999; background: linear-gradient(45deg, var(--gold), var(--gold-light)); color: var(--black); padding: 1rem 2rem; border-radius: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; border: none; box-shadow: 0 0 30px rgba(212,175,55,.4); animation: pulse 2s infinite; }
        .landing-m3 .install-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.85); z-index: 9999; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(8px); }
        .landing-m3 .install-modal { background: linear-gradient(135deg, #0a0a0a, #151515); border: 1px solid rgba(212,175,55,0.3); max-width: 420px; width: 90%; padding: 2.5rem; text-align: center; position: relative; box-shadow: 0 25px 80px rgba(212,175,55,0.15); }
        .landing-m3 .install-modal::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent, var(--gold), transparent); }
        .landing-m3 .install-modal-logo { width: 80px; height: 80px; margin: 0 auto 1.5rem; border: 2px solid rgba(212,175,55,0.3); padding: 8px; background: #050505; }
        .landing-m3 .install-modal-logo img { width: 100%; height: 100%; object-fit: contain; }
        .landing-m3 .install-modal h3 { font-size: 1.5rem; margin-bottom: 0.5rem; background: linear-gradient(to right, var(--white), var(--gold)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .landing-m3 .install-modal p { color: rgba(255,255,255,0.6); font-size: 0.9rem; margin-bottom: 2rem; }
        .landing-m3 .install-options { display: flex; flex-direction: column; gap: 1rem; }
        .landing-m3 .install-option { display: flex; align-items: center; gap: 1rem; padding: 1rem 1.25rem; background: rgba(255,255,255,0.03); border: 1px solid rgba(212,175,55,0.15); cursor: pointer; transition: var(--transition); text-align: left; }
        .landing-m3 .install-option:hover { border-color: var(--gold); background: rgba(212,175,55,0.08); transform: translateX(5px); }
        .landing-m3 .install-option-icon { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(212,175,55,0.1); color: var(--gold); flex-shrink: 0; }
        .landing-m3 .install-option-text h4 { color: var(--white); font-size: 0.95rem; font-weight: 600; margin-bottom: 0.25rem; font-family: Inter, sans-serif; }
        .landing-m3 .install-option-text p { color: rgba(255,255,255,0.5); font-size: 0.75rem; margin: 0; }
        .landing-m3 .install-modal-close { position: absolute; top: 1rem; right: 1rem; background: none; border: none; color: rgba(255,255,255,0.4); cursor: pointer; font-size: 1.5rem; }
        .landing-m3 .install-modal-close:hover { color: var(--gold); }
        .landing-m3 .ios-instructions { background: rgba(212,175,55,0.05); border: 1px solid rgba(212,175,55,0.2); padding: 1rem; margin-top: 1rem; text-align: left; }
        .landing-m3 .ios-instructions p { font-size: 0.8rem; color: rgba(255,255,255,0.7); margin: 0.5rem 0; }
        .landing-m3 .ios-instructions strong { color: var(--gold); }
        @media(max-width:992px) { 
          .landing-m3 .hero h1 { font-size: 3.5rem; } 
          .landing-m3 .about-content, .landing-m3 .luxury-content { grid-template-columns: 1fr; } 
          .landing-m3 .about-image, .landing-m3 .luxury-image { height: 400px; } 
          .landing-m3 .footer-grid { grid-template-columns: 1fr 1fr; gap: 2rem; } 
        }
        @media(max-width:768px) {
          .landing-m3 .nav-links { position: fixed; top: 0; right: -100%; width: 70%; height: 100vh; background: rgba(0,0,0,.95); flex-direction: column; padding: 5rem 2rem; transition: right .3s; z-index: 999; }
          .landing-m3 .nav-links.active { right: 0; }
          .landing-m3 .mobile-menu-btn { display: block; }
          .landing-m3 .hero h1 { font-size: 2.8rem; }
          .landing-m3 .section-title h2 { font-size: 2.5rem; }
          .landing-m3 .fixed-cta-content { flex-direction: column; text-align: center; gap: 0.5rem; }
          .landing-m3 .footer-grid { grid-template-columns: 1fr; gap: 2rem; text-align: center; }
          .landing-m3 .contact-grid { grid-template-columns: 1fr; }
          .landing-m3 .app-access-cta { top: auto; bottom: 70px; right: 10px; left: 10px; text-align: center; }
          .landing-m3 .pwa-banner { flex-direction: column; gap: 1rem; text-align: center; }
        }
      `}</style>

      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Install App Modal - Elegant Design */}
      {showInstallModal && (
        <div className="install-modal-overlay" onClick={() => setShowInstallModal(false)}>
          <div className="install-modal" onClick={e => e.stopPropagation()}>
            <button className="install-modal-close" onClick={() => setShowInstallModal(false)}>&times;</button>
            <div className="install-modal-logo">
              <img src="/m3-logo.png" alt="M3" />
            </div>
            <h3>Obtener M3</h3>
            <p>Elige como quieres acceder a la plataforma gastronomica</p>
            
            <div className="install-options">
              {/* Option 1: Install as App - always show */}
              <div className="install-option" onClick={canInstall ? handleInstall : () => {}} data-testid="install-app-option" style={{ cursor: canInstall ? 'pointer' : 'default' }}>
                <div className="install-option-icon">
                  <Download size={20} />
                </div>
                <div className="install-option-text">
                  <h4>Instalar como App</h4>
                  {canInstall ? (
                    <p>Toca para instalar en tu dispositivo</p>
                  ) : isIOS ? (
                    <p>Safari → Compartir → Anadir a inicio</p>
                  ) : isAndroid ? (
                    <p>Chrome → Menu (3 puntos) → Instalar</p>
                  ) : (
                    <p>Usa el menu de tu navegador para instalar</p>
                  )}
                </div>
              </div>
              
              {/* iOS/Android detailed instructions */}
              {(isIOS || isAndroid) && !canInstall && (
                <div className="ios-instructions">
                  {isIOS ? (
                    <>
                      <p><strong>Pasos para iPhone/iPad:</strong></p>
                      <p>1. Toca el icono de compartir (cuadro con flecha)</p>
                      <p>2. Desplaza y selecciona "Anadir a inicio"</p>
                      <p>3. Toca "Anadir" para confirmar</p>
                    </>
                  ) : (
                    <>
                      <p><strong>Pasos para Android:</strong></p>
                      <p>1. Toca los 3 puntos (menu) arriba derecha</p>
                      <p>2. Selecciona "Instalar aplicacion"</p>
                      <p>3. Confirma la instalacion</p>
                    </>
                  )}
                </div>
              )}
              
              {/* Option 2: Continue to App */}
              <div className="install-option" onClick={() => { setShowInstallModal(false); navigate('/auth'); }} data-testid="continue-app-option">
                <div className="install-option-icon">
                  <Smartphone size={20} />
                </div>
                <div className="install-option-text">
                  <h4>Continuar a la App</h4>
                  <p>Acceder ahora en el navegador</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="nav-header">
        <div className="container header-container">
          <a href="#home" className="logo">M3</a>
          <nav className="nav-links" id="navLinks">
            <a href="#about">Sobre nosotros</a>
            <a href="#services">Servicios</a>
            <a href="#packages">Paquetes</a>
            <a href="#luxury">Lujo</a>
            <a href="#consultation">Consulta</a>
            <button 
              onClick={() => setShowInstallModal(true)} 
              className="hero-cta hero-cta-app"
              data-testid="nav-app-btn"
              style={{ padding: '0.6rem 1.5rem', fontSize: '0.85rem' }}
            >
              ACCEDER A LA APP
            </button>
          </nav>
          <select className="language-selector" id="languageSelector" aria-label="Seleccionar idioma">
            <option value="es">ES</option>
            <option value="en">EN</option>
            <option value="fr">FR</option>
            <option value="ar">العربية</option>
            <option value="zh">中文</option>
          </select>
        </div>
      </header>

      {/* Hero */}
      <section className="hero" id="home">
        <div className="hero-image"></div>
        <div className="container">
          <div className="hero-content">
            <h1>Transformando la hospitalidad con excelencia</h1>
            <p>Consultoria culinaria estrategica y soluciones de hospitalidad de lujo para las marcas mas exigentes.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <button 
                onClick={() => setShowInstallModal(true)} 
                className="hero-cta hero-cta-app"
                data-testid="hero-cta-btn"
              >
                ACCEDER A LA APP
              </button>
              <a href="#packages" className="hero-cta hero-cta-app">
                DESCUBRE NUESTROS SERVICIOS
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about">
        <div className="container">
          <div className="section-title">
            <h2>Sobre nosotros</h2>
            <p>M3 CORPORATIVE GROUP representa la cumbre de la consultoria estrategica en hospitalidad.</p>
          </div>
          <div className="about-content">
            <div className="about-image"></div>
            <div className="about-text">
              <h3>M3 CORPORATIVE GROUP</h3>
              <p style={{ marginBottom: '1rem', color: 'var(--gray-light)' }}>Con mas de dos decadas en los niveles mas altos de la hospitalidad global, hemos desarrollado pericia inigualable en desarrollo de marcas de lujo, estrategia culinaria y excelencia operativa.</p>
              <p style={{ marginBottom: '1rem', color: 'var(--gray-light)' }}>Nuestra metodologia combina precision analitica con sensibilidad creativa de casas de lujo, entregando resultados transformadores.</p>
              <p style={{ color: 'var(--gray-light)' }}>Atendemos a una clientela exclusiva: hoteles de lujo, restaurantes de alta cocina e inversores premium que exigen perfeccion.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" style={{ background: 'rgba(10,10,10,0.5)' }}>
        <div className="container">
          <div className="section-title">
            <h2>Nuestros Servicios</h2>
            <p>Soluciones integrales disenadas para el sector de la hospitalidad de lujo</p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <h3>Consultoria Estrategica</h3>
              <p style={{ color: 'var(--gray-light)' }}>Desarrollo integral de estrategia de negocio, posicionamiento en el mercado y analisis competitivo para marcas de hospitalidad de lujo.</p>
            </div>
            <div className="service-card">
              <h3>Direccion Culinaria</h3>
              <p style={{ color: 'var(--gray-light)' }}>Desarrollo completo de programas culinarios, ingenieria de menus y liderazgo de equipos de chefs para establecimientos de alta cocina.</p>
            </div>
            <div className="service-card">
              <h3>Desarrollo de Marca de Lujo</h3>
              <p style={{ color: 'var(--gray-light)' }}>Creacion de identidad de marca, diseno de experiencia de huespedes e implementacion de estandares de servicio de lujo.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Clients */}
      <section>
        <div className="container">
          <div className="section-title">
            <h2>Clientes Premium</h2>
            <p>Confiado por las marcas de hospitalidad mas exclusivas del mundo</p>
          </div>
          <div className="clients-grid">
            {[
              { img: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?q=80&w=600&auto=format&fit=crop', name: 'Europe Hospitality Group' },
              { img: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=600&auto=format&fit=crop', name: 'Caribbean Culinary Luxury' },
              { img: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?q=80&w=600&auto=format&fit=crop', name: 'Global Hospitality Investments' },
              { img: 'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?q=80&w=600&auto=format&fit=crop', name: 'Caribbean Resort Collective' },
              { img: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=600&auto=format&fit=crop', name: 'Fine Dining International' },
              { img: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?q=80&w=600&auto=format&fit=crop', name: 'Asia-Pacific Resort Operator' },
            ].map((client, i) => (
              <div className="client-item" key={i}>
                <div className="client-logo">
                  <img src={client.img} alt={client.name} />
                </div>
                <p style={{ fontSize: '0.9rem', color: 'var(--gray-light)' }}>{client.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" style={{ background: 'rgba(10,10,10,0.5)' }}>
        <div className="container">
          <div className="section-title">
            <h2>Paquetes Premium</h2>
            <p>Paquetes de consultoria exclusivos disenados para la excelencia en hospitalidad de lujo</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Starter */}
            <div className="package-card">
              <div style={{ marginBottom: '0.5rem' }}>⭐</div>
              <h3 className="package-title">INICIAL</h3>
              <div className="package-subtitle">"Marco de Rentabilidad Inicial" – USD 997</div>
              <p style={{ fontSize: '0.9rem', fontStyle: 'italic', color: 'var(--gold)', marginBottom: '1rem' }}>"El punto de entrada al juego serio. Optimiza. Cobra. Gana."</p>
              <div className="package-price">$997</div>
              <ul className="package-features">
                <li>Sistema de Recetas Rentables</li>
                <li>Fichas tecnicas profesionales</li>
                <li>Calculadora de coste por porcion</li>
                <li>Plantilla de ingenieria de menus</li>
                <li>Control de desperdicios</li>
              </ul>
              <a href="https://diagnostico.m3corporativegroup.com" className="package-btn" target="_blank" rel="noopener noreferrer">Diagnostico Estrategico</a>
            </div>

            {/* Business Elite */}
            <div className="package-card">
              <div style={{ marginBottom: '0.5rem' }}>⭐⭐</div>
              <h3 className="package-title">NEGOCIO ELITE</h3>
              <div className="package-subtitle">"Suite de Rentabilidad Elite" – USD 3,000</div>
              <p style={{ fontSize: '0.9rem', fontStyle: 'italic', color: 'var(--gold)', marginBottom: '1rem' }}>"De restaurante normal a operacion disciplinada y predecible."</p>
              <div className="package-price">$3,000</div>
              <ul className="package-features">
                <li>Incluye TODO Inicial</li>
                <li>Sistema Maestro de Rentabilidad</li>
                <li>Precios Estrategicos + Ingenieria de Menus</li>
                <li>Branding Profesional Nivel Revista</li>
                <li>Sistema Inteligente de Control de Inventario</li>
              </ul>
              <a href="https://diagnostico.m3corporativegroup.com" className="package-btn" target="_blank" rel="noopener noreferrer">Diagnostico Estrategico</a>
            </div>

            {/* Hotel & Signature */}
            <div className="package-card">
              <div style={{ marginBottom: '0.5rem' }}>⭐⭐⭐</div>
              <h3 className="package-title">HOTEL & RESTAURANTE FIRMA</h3>
              <div className="package-subtitle">"Sistema de Hospitalidad Firma" – USD 8,000 a 15,000</div>
              <div className="package-price">$8,000–15,000</div>
              <ul className="package-features">
                <li>Incluye TODO Negocio Elite</li>
                <li>Arquitectura Completa de Costes</li>
                <li>Desarrollo de Recetas Premium</li>
                <li>Estrategia de Rentabilidad Hotelera</li>
                <li>Manual Operativo Completo (EN / ES)</li>
              </ul>
              <a href="https://diagnostico.m3corporativegroup.com" className="package-btn" target="_blank" rel="noopener noreferrer">Diagnostico Estrategico</a>
            </div>

            {/* Black Label */}
            <div className="package-card black-label-special">
              <div style={{ marginBottom: '0.5rem' }}>⭐⭐⭐⭐</div>
              <h3 className="package-title">BLACK LABEL POR M3</h3>
              <div className="package-subtitle">"BLACK LABEL — Edicion CEO" – USD 50,000</div>
              <p style={{ fontSize: '0.9rem', fontStyle: 'italic', color: 'var(--gold-light)', marginBottom: '1rem' }}>"La Experiencia Definitiva. Excelencia Hecha a Mano."</p>
              <div className="package-price">$50,000</div>
              <ul className="package-features">
                <li>Arquitectura Financiera Integral (60 meses)</li>
                <li>Sistema Ejecutivo con IA y analisis en tiempo real</li>
                <li>Creacion de Marca Cinematografica</li>
                <li>Asesoramiento Directo Estilo CEO (semanal)</li>
                <li>Diseno y optimizacion de cocinas</li>
                <li>Benchmarking Global</li>
              </ul>
              <a href="https://diagnostico.m3corporativegroup.com" className="package-btn" target="_blank" rel="noopener noreferrer">Reservar Experiencia Black Label</a>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury */}
      <section id="luxury">
        <div className="container">
          <div className="section-title">
            <h2>Hospitalidad de Lujo</h2>
            <p>Creando experiencias inolvidables para los huespedes mas exigentes del mundo</p>
          </div>
          <div className="luxury-content">
            <div className="luxury-text">
              <h3 style={{ fontSize: '2rem', marginBottom: '1.5rem', background: 'linear-gradient(to right, var(--white), var(--gold))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Excelencia en Cada Detalle</h3>
              <p style={{ marginBottom: '1rem', color: 'var(--gray-light)' }}>Nuestras soluciones se disenan con atencion al detalle y estandares intransigentes que definen las marcas mas prestigiosas.</p>
              <p style={{ marginBottom: '1rem', color: 'var(--gray-light)' }}>Combinamos la excelencia operacional con vision artistica para que cada punto de contacto refleje verdadero lujo.</p>
              <p style={{ color: 'var(--gray-light)' }}>Ya sea un nuevo concepto o una elevacion de propiedad, brindamos guia estrategica y direccion creativa.</p>
            </div>
            <div className="luxury-image"></div>
          </div>
        </div>
      </section>

      {/* Consultation */}
      <section className="consultation-section" id="consultation">
        <div className="container" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', color: 'var(--gold)', marginBottom: '1rem' }}>✦</div>
          <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>AGENDA UNA CONSULTA</h2>
          <p style={{ color: 'var(--gray-light)', marginBottom: '2rem' }}>Tu acceso directo a consultoria de clase mundial.</p>
          <div style={{ fontSize: '3rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '0.5rem' }}>$500</div>
          <p style={{ fontSize: '0.9rem', color: 'var(--gray-light)', marginBottom: '2rem' }}>Una inversion estrategica en el futuro de tu negocio</p>
          <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'left' }}>
            <p style={{ color: 'var(--gray-light)', marginBottom: '1rem' }}>En 60 minutos analizamos tu operacion y definimos oportunidades accionables.</p>
            <ul style={{ listStyle: 'none', marginBottom: '2rem' }}>
              <li style={{ padding: '0.5rem 0', color: 'var(--gray-light)', paddingLeft: '1.5rem', position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: 'var(--gold)' }}>✓</span> Analizo tu situacion actual</li>
              <li style={{ padding: '0.5rem 0', color: 'var(--gray-light)', paddingLeft: '1.5rem', position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: 'var(--gold)' }}>✓</span> Identifico fugas y oportunidades</li>
              <li style={{ padding: '0.5rem 0', color: 'var(--gray-light)', paddingLeft: '1.5rem', position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: 'var(--gold)' }}>✓</span> Proyectamos potencial de rentabilidad</li>
              <li style={{ padding: '0.5rem 0', color: 'var(--gray-light)', paddingLeft: '1.5rem', position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: 'var(--gold)' }}>✓</span> Plan de accion 30–90 dias</li>
            </ul>
          </div>
          <a href="https://diagnostico.m3corporativegroup.com" className="consultation-cta-btn" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>Reserva tu Consulta</a>
        </div>
      </section>

      {/* Contact */}
      <section id="contact">
        <div className="container">
          <div className="section-title">
            <h2>Contacto</h2>
            <p>Comienza tu viaje hacia la excelencia en hospitalidad</p>
          </div>
          <div className="contact-grid">
            <div style={{ paddingRight: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: 'var(--gold)' }}>Ponte en Contacto</h3>
              <div style={{ marginBottom: '1rem' }}>
                <a href="mailto:ceo@m3corporativegroup.com" className="package-btn" style={{ width: 'auto', display: 'inline-block' }}>Enviar Email</a>
              </div>
              <div>
                <a href="https://wa.me/18493707175" target="_blank" rel="noopener noreferrer" className="package-btn" style={{ width: 'auto', display: 'inline-block' }}>WhatsApp</a>
              </div>
            </div>
            <div className="contact-form">
              <h3 style={{ fontSize: '1.2rem', marginBottom: '1.5rem', color: 'var(--white)' }}>Envia un Mensaje</h3>
              <form action="https://formspree.io/f/xvzbraqk" method="POST">
                <input type="text" name="name" placeholder="Nombre Completo" required />
                <input type="email" name="email" placeholder="Tu correo electronico" required />
                <textarea name="message" placeholder="Mensaje" rows={4} required style={{ resize: 'vertical' }}></textarea>
                <button type="submit" className="package-btn">Enviar Mensaje</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container">
          <div className="footer-grid">
            <div className="footer-column">
              <div style={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '1px', background: 'linear-gradient(to right, var(--white), var(--gold))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '1rem' }}>M3</div>
              <p>Transformando marcas de hospitalidad en lideres mundiales a traves de estrategia financiera, excelencia operativa y branding de lujo.</p>
              <div className="footer-socials">
                <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer">in</a>
                <a href="https://www.instagram.com/m3corporativegroup" target="_blank" rel="noopener noreferrer">@m3corporativegroup</a>
              </div>
            </div>
            <div className="footer-column">
              <h3>Explorar</h3>
              <ul>
                <li><a href="#about">Sobre Nosotros</a></li>
                <li><a href="#services">Servicios</a></li>
                <li><a href="#packages">Paquetes</a></li>
                <li><a href="#luxury">Lujo</a></li>
              </ul>
            </div>
            <div className="footer-column">
              <h3>Contacto</h3>
              <div style={{ marginBottom: '0.5rem' }}>
                <a href="mailto:ceo@m3corporativegroup.com" className="package-btn" style={{ width: 'auto', display: 'inline-block', padding: '0.5rem 1rem', fontSize: '0.8rem' }}>Email</a>
              </div>
              <div>
                <a href="https://wa.me/18493707175" target="_blank" rel="noopener noreferrer" className="package-btn" style={{ width: 'auto', display: 'inline-block', padding: '0.5rem 1rem', fontSize: '0.8rem' }}>WhatsApp</a>
              </div>
            </div>
            <div className="footer-column">
              <h3>Legal</h3>
              <ul>
                <li><a href="#">Politica de Privacidad</a></li>
                <li><a href="#">Terminos de Servicio</a></li>
              </ul>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(212,175,55,0.1)', paddingTop: '2rem', textAlign: 'center' }}>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>&copy; 2025 M3 CORPORATIVE GROUP. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Fixed CTA */}
      <div className="fixed-cta">
        <div className="fixed-cta-content">
          <div>
            <span style={{ color: 'var(--gold)', fontWeight: 600 }}>CONSULTORIA PREMIUM</span>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray-light)', flexWrap: 'wrap' }}>
              <span><strong>Inicial:</strong> $997</span>
              <span><strong>Elite:</strong> $3,000</span>
              <span><strong>Hotel:</strong> $8,000+</span>
            </div>
          </div>
          <button className="fixed-cta-button" onClick={() => navigate('/auth')}>
            ACCEDER A LA APP
          </button>
        </div>
      </div>
    </div>
  );
}
